import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, CalendarDays, MessageSquare, ChevronDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { specMove } from "./glass";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAL_URL = "https://cal.com/asvida-senindu-vxwyoz/15min";
const PLATFORMS = ["WhatsApp", "Telegram", "Phone call", "Instagram DM", "X DM", "LinkedIn DM"];

const inputCls =
  "w-full bg-white/60 backdrop-blur-md rounded-2xl px-4 py-3.5 text-[15px] text-[#1d1d1f] placeholder:text-[#aeaeb2] outline-none border border-black/[0.06] shadow-[inset_0_1px_2px_rgba(0,0,0,0.03)] focus:border-[#1d1d1f]/30 transition-colors duration-200";

export default function ContactModal({ open, onClose }) {
  const [tab, setTab] = useState("form");
  const [form, setForm] = useState({ name: "", platform: "WhatsApp", handle: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setTab("form");
    }, 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="contact-modal"
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-md" onClick={close} />
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 48, opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="glass-sheen relative w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-t-[32px] sm:rounded-[32px] bg-white/70 backdrop-blur-2xl border border-white/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),0_40px_120px_rgba(0,0,0,0.3)]"
          >
            <div className="relative flex items-center justify-between px-7 sm:px-9 pt-7">
              <span className="text-sm font-medium text-[#86868b]">Let's talk</span>
              <button
                onClick={close}
                data-testid="contact-modal-close"
                className="glass-soft flex h-8 w-8 items-center justify-center rounded-full text-[#6e6e73] transition-transform duration-200 hover:scale-110 active:scale-90"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="relative px-7 sm:px-9 pt-5 flex gap-2">
              <button
                onClick={() => setTab("form")}
                data-testid="contact-tab-form"
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-medium transition-all duration-300 active:scale-95 ${
                  tab === "form" ? "glass-strong text-white" : "glass-soft text-[#6e6e73] hover:text-[#1d1d1f]"
                }`}
              >
                <MessageSquare className="h-3.5 w-3.5" /> Leave your details
              </button>
              <button
                onClick={() => setTab("call")}
                data-testid="contact-tab-calendly"
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-medium transition-all duration-300 active:scale-95 ${
                  tab === "call" ? "glass-strong text-white" : "glass-soft text-[#6e6e73] hover:text-[#1d1d1f]"
                }`}
              >
                <CalendarDays className="h-3.5 w-3.5" /> Book a 15-min call
              </button>
            </div>

            <div className="relative px-7 sm:px-9 py-8">
              {tab === "form" ? (
                done ? (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="py-10 text-center"
                    data-testid="contact-form-success"
                  >
                    <span className="glass-strong mx-auto flex h-14 w-14 items-center justify-center rounded-full">
                      <Check className="h-6 w-6 text-white" />
                    </span>
                    <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-[#1d1d1f]">
                      Got it.
                    </h3>
                    <p className="mt-3 text-[15px] text-[#6e6e73] max-w-sm mx-auto leading-relaxed">
                      We'll personally reach out to you on {form.platform} shortly. Talk soon.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="space-y-5">
                    <p className="text-[15px] text-[#6e6e73] leading-relaxed">
                      Drop your number or the handle you prefer — we'll personally reach out.
                    </p>
                    <div>
                      <label className="block text-[13px] font-medium text-[#86868b] mb-2">
                        Your name
                      </label>
                      <input
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Jane Founder"
                        className={inputCls}
                        data-testid="contact-form-name-input"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-medium text-[#86868b] mb-2">
                          Where should we reach you?
                        </label>
                        <div className="relative">
                          <select
                            value={form.platform}
                            onChange={set("platform")}
                            className={`${inputCls} appearance-none cursor-pointer pr-10 [&>option]:bg-white`}
                            data-testid="contact-form-goal-select"
                          >
                            {PLATFORMS.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868b]"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[13px] font-medium text-[#86868b] mb-2">
                          Number or handle
                        </label>
                        <input
                          required
                          value={form.handle}
                          onChange={set("handle")}
                          placeholder="+1 555 000 0000 or @handle"
                          className={inputCls}
                          data-testid="contact-form-handle-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[13px] font-medium text-[#86868b] mb-2">
                        What are you trying to achieve? (optional)
                      </label>
                      <textarea
                        rows={4}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Where you're at, the goal, and what's getting in the way."
                        className={`${inputCls} resize-none`}
                        data-testid="contact-form-message-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      onMouseMove={specMove}
                      data-testid="contact-form-submit-button"
                      className="glass-strong glass-sheen glass-spec w-full rounded-full px-8 py-4 text-[15px] font-medium text-white transition-transform duration-200 hover:scale-[1.02] active:scale-[0.96] disabled:opacity-50"
                    >
                      {sending ? "Sending..." : "Send — we'll reach out"}
                    </button>
                  </form>
                )
              ) : (
                <div>
                  <p className="text-[15px] text-[#6e6e73] leading-relaxed mb-5">
                    Prefer to talk live? Grab 15 minutes directly on the calendar.
                  </p>
                  <div className="overflow-hidden rounded-[22px] border border-black/[0.06]">
                    <iframe
                      src={CAL_URL}
                      title="Book a 15-minute call with VONTRAIL"
                      className="h-[480px] w-full"
                      data-testid="calendly-iframe"
                    />
                  </div>
                  <a
                    href={CAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-1 text-[13px] font-medium text-[#86868b] hover:text-[#1d1d1f] transition-colors"
                    data-testid="calendly-external-link"
                  >
                    Open in new tab
                  </a>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
